import React, { Component, useState, useEffect } from 'react';
import HelpText from '../lib/help-text';
import { Autocomplete, TextField, IconButton, Grid, Tooltip, Tab, Tabs, CircularProgress } from '@mui/material';
import { Button, DialogContainer } from 'react-md';
import { MdEdit, MdDriveFileMove, MdInfoOutline } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";

import SFCustomTable from '../component/custom/SFCustomTable';
import ApiRequestService from '../component/ApiRequestService';
import AssignmentDetailModal from './AssignmentMatrix/Modal/AssignmentDetailModal';
import SFConfirmAlert from '../component/custom/SFConfirmAlert';
import { NotificationManager } from 'react-notifications';
import SecureShow from '../component/SecureShow';
import { MdContentCopy } from "react-icons/md";
import DuplicateTutorial from './DuplicateTutorial';
import CreateExplanation from './CreateExplanation';
import ChangeTutorialLocationDialog from './ChangeTutorialLocationDialog';
import { useTutorialSync, tutorialSyncColumn, TutorialUnsyncedDialog, ReceivedTutorialActionsCell } from './Tutorials/TutorialSync';

const AllTutorialsTab = ({ onCountChange, tutorialsLocationHelpText }) => {

    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [allTutorials, setAllTutorials] = useState([]);
    const [filteredTutorials, setFilteredTutorials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTutorialDialog, setShowTutorialDialog] = useState(false);
    const [selectedTutorial, setSelectedTutorial] = useState(null);
    // Set while a received tutorial is open, so the dialog only shows it.
    const [viewOnly, setViewOnly] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [showChangeLocationDialog, setShowChangeLocationDialog] = useState(false);
    const [tutorialKey, setTutorialKey] = useState("");

    const tutorialSync = useTutorialSync();

    const columns = [
        {
            name: <div className='sf-data-table-header'>ID</div>,
            selector: row => row.quiz_id,
            sortable: true,
            width: '100px',
        },
        {
            name: <div className='sf-data-table-header'>Tutorial Name</div>,
            selector: row => row.quiz_name,
            sortable: true,
            width: '250px',
        },
        {
            name: <div className='sf-data-table-header'>Subject</div>,
            selector: row => row.subject_name,
            sortable: true,
        },
        {
            name: <div className='sf-data-table-header'>Topic</div>,
            selector: row => row.topic_name,
            sortable: true,
        },
        {
            name: <div className='sf-data-table-header'>Category</div>,
            selector: row => row.category_name ? row.category_name : "All",
            sortable: true,
            wrap: true,
        },
        {
            name: <div className='sf-data-table-header'>Subcategory</div>,
            selector: row => row.subcategory_name ? row.subcategory_name : "All",
            sortable: true,
            wrap: true,
        },
        tutorialSyncColumn(tutorialSync),
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            cell: (row) => {
                // Received from another university - only its owner edits it.
                if (row.is_synced) {
                    return <ReceivedTutorialActionsCell onView={() => {
                        setTutorialKey(row.tutorial_key);
                        getQuizDetail(row, true);
                    }} />;
                }
                return (
                    <div>
                        <Tooltip title="Edit" arrow placement="top">
                            <IconButton onClick={() => {
                                setTutorialKey(row.tutorial_key)
                                getQuizDetail(row);
                            }}>
                                <MdEdit color='#3d5e9c' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Change Location" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedTutorial(row);
                                setShowChangeLocationDialog(true);
                            }}>
                                <MdDriveFileMove color='#e67e22' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedTutorial(row);
                                setShowDuplicateDialog(true);
                            }} >
                                <MdContentCopy color='#3d5e9c' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedTutorial(row);
                                setShowConfirmDelete(true);
                            }}>
                                <FiTrash2 color='#bb0000' />
                            </IconButton>
                        </Tooltip>
                    </div>
                )
            },
        },
    ].filter(column => tutorialSync.syncAccess || column.id !== 'sync');

    useEffect(() => {
        getSubjects();
        getAllTutorials();
        tutorialSync.loadSyncStatus();
    }, []);

    const getSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects'
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubjects(res.data.data.subject_names);
            }
        });
    };

    const getTopics = (subjectId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: { subject_id: subjectId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        });
    };

    const getCategories = (subjectId, topicId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: { subject_id: subjectId, topic_id: topicId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        });
    };

    const getSubcategories = (subjectId, topicId, categoryId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: { subject_id: subjectId, topic_id: topicId, category_id: categoryId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubcategories(res.data.data.subCategory_names);
            }
        });
    };

    const getAllTutorials = () => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/getAllTutorials',
        }).then(res => {
            if (res && res.data && res.data.data) {
                setAllTutorials(res.data.data);
                setFilteredTutorials(res.data.data);
                if (onCountChange) onCountChange(res.data.data.length);
            } else {
                setAllTutorials([]);
                setFilteredTutorials([]);
                if (onCountChange) onCountChange(0);
            }
            // A save, move or duplicate of a synced tutorial queues a push.
            tutorialSync.reloadSyncStatus();
        }).finally(() => {
            setLoading(false);
        });
    };

    const deleteTutorial = (quizID) => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/softDeleteQuiz',
            data: { quiz_id: quizID }
        }).then(res => {
            if (res && res.data) {
                if (res.data.message !== "") {
                    NotificationManager.error(res.data.message, "Unable to delete", 4000);
                    return;
                }
                setAllTutorials(prev => prev.filter(tut => tut.quiz_id !== selectedTutorial.quiz_id));
                setFilteredTutorials(prev => prev.filter(tut => tut.quiz_id !== selectedTutorial.quiz_id));
                tutorialSync.reloadSyncStatus();
                setShowConfirmDelete(false);
                setSelectedTutorial(null);
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    const getQuizDetail = (assign, readOnly = false) => {
        ApiRequestService({
            method: 'post',
            url: '/getQuizById',
            data: {
                course_id: 0,
                section_id: 0,
                quiz_id: assign.quiz_id,
            }
        }).then(res => {
            if (res.data.data !== null && res.data.data !== undefined && res.data.data.quiz_id) {
                setSelectedTutorial(res.data.data);
                setViewOnly(readOnly);
                setShowTutorialDialog(true);
            }
        });
    };

    const handleSubjectSelect = (_, value) => {
        setSelectedSubject(value);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        if (value) {
            getTopics(value.id);
        }
    };

    const handleTopicSelect = (_, value) => {
        setSelectedTopic(value);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setCategories([]);
        setSubcategories([]);
        if (value) {
            getCategories(value.data.subject_id, value.id);
        }
    };

    const handleCategorySelect = (_, value) => {
        setSelectedCategory(value);
        setSelectedSubcategory(null);
        setSubcategories([]);
        if (value) {
            getSubcategories(value.data.subject_id, value.data.topic_id, value.id);
        }
    };

    const handleSubcategorySelect = (_, value) => {
        setSelectedSubcategory(value);
    };

    const buildTutorialKey = () => {
        if (!selectedSubject) return "";
        let key = `${selectedSubject.id}`;
        if (selectedTopic) {
            key += `_${selectedTopic.id}`;
        }
        if (selectedCategory) {
            key += `_${selectedCategory.id}`;
        }
        if (selectedSubcategory) {
            key += `_${selectedSubcategory.id}`;
        }
        return key;
    };

    useEffect(() => {
        const keyPrefix = buildTutorialKey();
        if (!keyPrefix) {
            setFilteredTutorials(allTutorials);
            return;
        }
        const filtered = allTutorials.filter(tut => {
            if (!tut.tutorial_key) return false;
            return tut.tutorial_key === keyPrefix || tut.tutorial_key.startsWith(keyPrefix + '_');
        });
        setFilteredTutorials(filtered);
    }, [selectedSubject, selectedTopic, selectedCategory, selectedSubcategory, allTutorials]);


    return (
        <div>
            <div className='mx-4 mt-3'>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>Tutorial Location</span>
                    <Tooltip title={<div style={{ fontSize: '13px' }}>{tutorialsLocationHelpText}
                        </div>} arrow placement="right">
                        <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}><MdInfoOutline size={18} color="#888" /></span>
                    </Tooltip>
                </div>
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={subjects ? subjects.map(subject => ({
                                id: subject.subject_id,
                                label: subject.subject_name,
                                data: subject,
                            })) : []}
                            value={selectedSubject}
                            onChange={handleSubjectSelect}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subject"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={topics ? topics.map(topic => ({
                                id: topic.topic_id,
                                label: topic.topic_name,
                                data: topic,
                            })) : []}
                            value={selectedTopic}
                            onChange={handleTopicSelect}
                            disabled={!selectedSubject}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Topic"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={categories ? categories.map(category => ({
                                id: category.category_id,
                                label: category.category_name,
                                data: category,
                            })) : []}
                            value={selectedCategory}
                            onChange={handleCategorySelect}
                            disabled={!selectedTopic}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={subcategories ? subcategories.map(subcategory => ({
                                id: subcategory.subcategory_id,
                                label: subcategory.subcategory_name,
                                data: subcategory,
                            })) : []}
                            value={selectedSubcategory}
                            onChange={handleSubcategorySelect}
                            disabled={!selectedCategory}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subcategory"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item>
                        <Button primary raised onClick={() => {
                            if (selectedSubject === null || selectedTopic === null) {
                                NotificationManager.error("Please atleast choose a subject and a topic")
                                return;
                            }
                            let key = buildTutorialKey();
                            setTutorialKey(key);
                            setSelectedTutorial(null);
                            setShowTutorialDialog(true);
                        }}>ADD NEW TUTORIAL</Button>
                    </Grid>
                </Grid>
            </div>

            <div style={{ marginTop: '1rem' }}>
                <SFCustomTable
                    loading={loading}
                    columns={columns}
                    selectableRows={false}
                    data={filteredTutorials}
                    topPagination={true}
                    bottomPagination={true}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchPlaceholder="Search by tutorial name"
                    searchByFields={["quiz_name"]}
                />
            </div>

            {showTutorialDialog ?
                <AssignmentDetailModal
                    isTutorial={true}
                    readOnly={viewOnly}
                    showModal={showTutorialDialog}
                    assignment={selectedTutorial}
                    handleClose={() => {
                        setShowTutorialDialog(false);
                        setViewOnly(false);
                        setSelectedTutorial(null);
                    }}
                    onSaveAssignment={() => {
                        getAllTutorials();
                    }}
                    course_id={null}
                    handleResetPreview={null}
                    onAfterPreviewClick={() => { }}
                    tutorialKey={tutorialKey}
                />
                : null}

            <DuplicateTutorial
                open={showDuplicateDialog}
                quizID={selectedTutorial?.quiz_id}
                onComplete={() => {
                    setShowDuplicateDialog(false);
                    setSelectedTutorial(null);
                    getAllTutorials();
                }}
                onCancel={() => {
                    setShowDuplicateDialog(false);
                    setSelectedTutorial(null);
                }}
            />

            <SFConfirmAlert
                open={showConfirmDelete}
                title={"Delete Tutorial"}
                description={`Are you sure you want to delete "${selectedTutorial?.quiz_name}"?`}
                onConfirm={() => {
                    if (selectedTutorial && selectedTutorial.quiz_id) {
                        deleteTutorial(selectedTutorial.quiz_id);
                    }
                }}
                loading={loading}
                onCancel={() => {
                    setShowConfirmDelete(false);
                    setSelectedTutorial(null);
                }}
            />

            <ChangeTutorialLocationDialog
                open={showChangeLocationDialog}
                item={selectedTutorial}
                type="tutorial"
                onClose={() => {
                    setShowChangeLocationDialog(false);
                    setSelectedTutorial(null);
                }}
                onSaved={() => {
                    setShowChangeLocationDialog(false);
                    setSelectedTutorial(null);
                    getAllTutorials();
                }}
            />

            <TutorialUnsyncedDialog tutorialSync={tutorialSync} />
        </div>
    );
};

const EditExplanationButton = ({ row, onEdit }) => {
    const [loading, setLoading] = useState(false);
    const handleClick = () => {
        setLoading(true);
        onEdit(row).finally(() => setLoading(false));
    };
    return (
        <Tooltip title="Edit" arrow placement="top">
            <IconButton onClick={handleClick} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <MdEdit color='#3d5e9c' />}
            </IconButton>
        </Tooltip>
    );
};

const AllExplanationsTab = ({ onCountChange, explanationsLocationHelpText }) => {

    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [allExplanations, setAllExplanations] = useState([]);
    const [filteredExplanations, setFilteredExplanations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExplanation, setSelectedExplanation] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showExplanationDialog, setShowExplanationDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newExplanationName, setNewExplanationName] = useState("");
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [duplicateExplanationName, setDuplicateExplanationName] = useState("");
    const [showChangeLocationDialog, setShowChangeLocationDialog] = useState(false);

    const addExplanation = () => {
        if (!newExplanationName || newExplanationName.trim() === "") {
            NotificationManager.error("Please enter a valid Explanation Name");
            return;
        }
        const payload = {
            subject_id: selectedSubject.id,
            topic_id: selectedTopic.id,
            category_id: selectedCategory ? selectedCategory.id : 0,
            subcategory_id: selectedSubcategory ? selectedSubcategory.id : 0,
            explanation_name: newExplanationName.trim(),
        };
        ApiRequestService({
            method: 'post',
            url: '/addExplanation',
            data: payload
        }).then(res => {
            if (res && res.data) {
                if (res.data.message !== null && res.data.message !== "") {
                    NotificationManager.error(res.data.message, "ERROR", 3000);
                    return;
                }
                if (res.data.data) {
                    const newExp = {
                        ...res.data.data,
                        subject_id: selectedSubject.id,
                        subject_name: selectedSubject.label,
                        topic_id: selectedTopic.id,
                        topic_name: selectedTopic.label,
                        category_id: selectedCategory ? selectedCategory.id : 0,
                        category_name: selectedCategory ? selectedCategory.label : "",
                        subcategory_id: selectedSubcategory ? selectedSubcategory.id : 0,
                        subcategory_name: selectedSubcategory ? selectedSubcategory.label : "",
                    };
                    setSelectedExplanation(newExp);
                    setShowExplanationDialog(true);
                    getAllExplanations();
                }
            }
        });
        setShowAddDialog(false);
        setNewExplanationName("");
    };

    const duplicateExplanation = () => {
        if (!duplicateExplanationName || duplicateExplanationName.trim() === "") {
            NotificationManager.error("Please enter a valid Explanation Name");
            return;
        }
        const payload = {
            explanation_id: selectedExplanation.explanation_id,
            subject_id: selectedExplanation.subject_id,
            topic_id: selectedExplanation.topic_id,
            category_id: selectedExplanation.category_id || 0,
            subcategory_id: selectedExplanation.subcategory_id || 0,
            explanation_name: duplicateExplanationName.trim(),
        };
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/duplicateExplanation',
            data: payload
        }).then(res => {
            if (res && res.data) {
                if (res.data.message !== null && res.data.message !== "") {
                    NotificationManager.error(res.data.message, "ERROR", 3000);
                    return;
                }
                getAllExplanations();
            }
        }).finally(() => {
            setLoading(false);
            setShowDuplicateDialog(false);
            setDuplicateExplanationName("");
            setSelectedExplanation(null);
        });
    };

    const getExplanationDetail = (row) => {
        return ApiRequestService({
            method: 'post',
            url: '/getExplanationByID',
            data: {
                explanation_id: row.explanation_id,
                subject_id: row.subject_id,
                topic_id: row.topic_id,
                category_id: row.category_id || 0,
                subcategory_id: row.subcategory_id || 0,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSelectedExplanation({ ...row, ...res.data.data });
                setShowExplanationDialog(true);
            }
        });
    };

    const columns = [
        {
            name: <div className='sf-data-table-header'>ID</div>,
            selector: row => row.explanation_id,
            sortable: true,
            width: '100px',
        },
        {
            name: <div className='sf-data-table-header'>Explanation Name</div>,
            selector: row => row.explanation_name,
            sortable: true,
            width: '250px',
        },
        {
            name: <div className='sf-data-table-header'>Subject</div>,
            selector: row => row.subject_name,
            sortable: true,
        },
        {
            name: <div className='sf-data-table-header'>Topic</div>,
            selector: row => row.topic_name,
            sortable: true,
        },
        {
            name: <div className='sf-data-table-header'>Category</div>,
            selector: row => row.category_name ? row.category_name : "All",
            sortable: true,
            wrap: true,
        },
        {
            name: <div className='sf-data-table-header'>Subcategory</div>,
            selector: row => row.subcategory_name ? row.subcategory_name : "All",
            sortable: true,
            wrap: true,
        },
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            cell: (row) => {
                return (
                    <div>
                        <EditExplanationButton row={row} onEdit={getExplanationDetail} />
                        <Tooltip title="Change Location" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedExplanation(row);
                                setShowChangeLocationDialog(true);
                            }}>
                                <MdDriveFileMove color='#e67e22' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedExplanation(row);
                                setDuplicateExplanationName("Copy " + row.explanation_name);
                                setShowDuplicateDialog(true);
                            }}>
                                <MdContentCopy color='#3d5e9c' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedExplanation(row);
                                setShowConfirmDelete(true);
                            }}>
                                <FiTrash2 color='#bb0000' />
                            </IconButton>
                        </Tooltip>
                    </div>
                )
            },
        },
    ];

    useEffect(() => {
        getSubjects();
        getAllExplanations();
    }, []);

    const getSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects'
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubjects(res.data.data.subject_names);
            }
        });
    };

    const getTopics = (subjectId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: { subject_id: subjectId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        });
    };

    const getCategories = (subjectId, topicId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: { subject_id: subjectId, topic_id: topicId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        });
    };

    const getSubcategories = (subjectId, topicId, categoryId) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: { subject_id: subjectId, topic_id: topicId, category_id: categoryId }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubcategories(res.data.data.subCategory_names);
            }
        });
    };

    const getAllExplanations = () => {
        setLoading(true);
        ApiRequestService({
            method: 'get',
            url: '/getAllExplanationsList',
        }).then(res => {
            if (res && res.data && res.data.data) {
                setAllExplanations(res.data.data);
                setFilteredExplanations(res.data.data);
                if (onCountChange) onCountChange(res.data.data.length);
            } else {
                setAllExplanations([]);
                setFilteredExplanations([]);
                if (onCountChange) onCountChange(0);
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    const deleteExplanation = (explanation) => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/deleteExplanation',
            data: {
                explanation_id: explanation.explanation_id,
                subject_id: explanation.subject_id,
                topic_id: explanation.topic_id,
                category_id: explanation.category_id,
                subcategory_id: explanation.subcategory_id,
            }
        }).then(res => {
            if (res && res.data) {
                const match = (exp) =>
                    exp.explanation_id === selectedExplanation.explanation_id &&
                    exp.subject_id === selectedExplanation.subject_id &&
                    exp.topic_id === selectedExplanation.topic_id &&
                    exp.category_id === selectedExplanation.category_id &&
                    exp.subcategory_id === selectedExplanation.subcategory_id;
                setAllExplanations(prev => prev.filter(exp => !match(exp)));
                setFilteredExplanations(prev => prev.filter(exp => !match(exp)));
                setShowConfirmDelete(false);
                setSelectedExplanation(null);
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleSubjectSelect = (_, value) => {
        setSelectedSubject(value);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        if (value) {
            getTopics(value.id);
        }
    };

    const handleTopicSelect = (_, value) => {
        setSelectedTopic(value);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setCategories([]);
        setSubcategories([]);
        if (value) {
            getCategories(value.data.subject_id, value.id);
        }
    };

    const handleCategorySelect = (_, value) => {
        setSelectedCategory(value);
        setSelectedSubcategory(null);
        setSubcategories([]);
        if (value) {
            getSubcategories(value.data.subject_id, value.data.topic_id, value.id);
        }
    };

    const handleSubcategorySelect = (_, value) => {
        setSelectedSubcategory(value);
    };

    useEffect(() => {
        if (!selectedSubject) {
            setFilteredExplanations(allExplanations);
            return;
        }
        const filtered = allExplanations.filter(exp => {
            if (exp.subject_id !== selectedSubject.id) return false;
            if (selectedTopic && exp.topic_id !== selectedTopic.id) return false;
            if (selectedCategory && exp.category_id !== selectedCategory.id) return false;
            if (selectedSubcategory && exp.subcategory_id !== selectedSubcategory.id) return false;
            return true;
        });
        setFilteredExplanations(filtered);
    }, [selectedSubject, selectedTopic, selectedCategory, selectedSubcategory, allExplanations]);


    return (
        <div>
            <div className='mx-4 mt-3'>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>Explanation Location</span>
                    <Tooltip title={<div style={{ fontSize: '13px' }}>{explanationsLocationHelpText}
                        </div>} arrow placement="right">
                        <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}><MdInfoOutline size={18} color="#888" /></span>
                    </Tooltip>
                </div>
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={subjects ? subjects.map(subject => ({
                                id: subject.subject_id,
                                label: subject.subject_name,
                                data: subject,
                            })) : []}
                            value={selectedSubject}
                            onChange={handleSubjectSelect}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subject"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={topics ? topics.map(topic => ({
                                id: topic.topic_id,
                                label: topic.topic_name,
                                data: topic,
                            })) : []}
                            value={selectedTopic}
                            onChange={handleTopicSelect}
                            disabled={!selectedSubject}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Topic"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={categories ? categories.map(category => ({
                                id: category.category_id,
                                label: category.category_name,
                                data: category,
                            })) : []}
                            value={selectedCategory}
                            onChange={handleCategorySelect}
                            disabled={!selectedTopic}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={subcategories ? subcategories.map(subcategory => ({
                                id: subcategory.subcategory_id,
                                label: subcategory.subcategory_name,
                                data: subcategory,
                            })) : []}
                            value={selectedSubcategory}
                            onChange={handleSubcategorySelect}
                            disabled={!selectedCategory}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subcategory"
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: "6px" },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item>
                        <Button primary raised onClick={() => {
                            if (!selectedSubject || !selectedTopic) {
                                NotificationManager.error("Please atleast choose a subject and a topic");
                                return;
                            }
                            setShowAddDialog(true);
                        }}>ADD NEW EXPLANATION</Button>
                    </Grid>
                </Grid>
            </div>

            <div style={{ marginTop: '2rem' }}>
                <SFCustomTable
                    loading={loading}
                    columns={columns}
                    selectableRows={false}
                    data={filteredExplanations}
                    topPagination={true}
                    bottomPagination={true}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchPlaceholder="Search by explanation name"
                    searchByFields={["explanation_name"]}
                />
            </div>

            <SFConfirmAlert
                open={showConfirmDelete}
                title={"Delete Explanation"}
                description={`Are you sure you want to delete "${selectedExplanation?.explanation_name}"?`}
                onConfirm={() => {
                    if (selectedExplanation && selectedExplanation.explanation_id) {
                        deleteExplanation(selectedExplanation);
                    }
                }}
                loading={loading}
                onCancel={() => {
                    setShowConfirmDelete(false);
                    setSelectedExplanation(null);
                }}
            />

            {showExplanationDialog && selectedExplanation ?
                <DialogContainer
                    id="explanation-edit-dialog"
                    aria-label="Edit Explanation"
                    visible={showExplanationDialog}
                    fullPage
                    focusOnMount={false}
                    onHide={() => {
                        setShowExplanationDialog(false);
                        setSelectedExplanation(null);
                    }}
                >
                    <div>
                        <Button icon onClick={() => {
                            setShowExplanationDialog(false);
                            setSelectedExplanation(null);
                        }}>
                            close
                        </Button>
                    </div>
                    <div style={{ overflow: "hidden" }}>
                        <CreateExplanation
                            selectedSubjectId={selectedExplanation.subject_id}
                            selectedTopicId={selectedExplanation.topic_id}
                            selectedCategoryId={selectedExplanation.category_id}
                            selectedSubCategoryId={selectedExplanation.subcategory_id}
                            selectedExplanationId={selectedExplanation.explanation_id}
                            selectedExplanationName={selectedExplanation.explanation_name}
                            selectedExplanationText={selectedExplanation.text}
                            selectedAltExplanationText={selectedExplanation.alt_explanation_text}
                            explanation={selectedExplanation}
                            selectedIndex={0}
                            close={() => {
                                setShowExplanationDialog(false);
                                setSelectedExplanation(null);
                                getAllExplanations();
                            }}
                            onUpdate={() => {
                                getAllExplanations();
                            }}
                            currSubject={true}
                            showPreview={false}
                        />
                    </div>
                </DialogContainer>
                : null}

            <DialogContainer
                id="add-explanation-dialog"
                visible={showAddDialog}
                modal
                onHide={() => {
                    setShowAddDialog(false);
                    setNewExplanationName("");
                }}
                width="500px"
                actions={[
                    <Button key="cancel" flat onClick={() => { setShowAddDialog(false); setNewExplanationName(""); }}>Cancel</Button>,
                    <Button key="add" primary raised onClick={addExplanation}>Add Explanation</Button>,
                ]}
            >
                <p style={{ fontSize: "medium" }}>
                    <b><label htmlFor="newExpName">New Explanation</label></b>
                </p>
                <TextField
                    id="newExpName"
                    fullWidth
                    size="small"
                    value={newExplanationName}
                    onChange={(e) => setNewExplanationName(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') addExplanation();
                    }}
                />
            </DialogContainer>

            <DialogContainer
                id="duplicate-explanation-dialog"
                visible={showDuplicateDialog}
                onHide={() => {
                    setShowDuplicateDialog(false);
                    setDuplicateExplanationName("");
                    setSelectedExplanation(null);
                }}
                width="500px"
                actions={[
                    <Button key="cancel" flat onClick={() => { setShowDuplicateDialog(false); setDuplicateExplanationName(""); setSelectedExplanation(null); }}>Cancel</Button>,
                    <Button key="dup" raised primary onClick={duplicateExplanation}>Duplicate</Button>,
                ]}
            >
                <p style={{ fontSize: "medium" }}>
                    <b><label htmlFor="dupExpName">Duplicate Explanation Name</label></b>
                </p>
                <TextField
                    id="dupExpName"
                    fullWidth
                    size="small"
                    value={duplicateExplanationName}
                    onChange={(e) => setDuplicateExplanationName(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') duplicateExplanation();
                    }}
                />
            </DialogContainer>

            <ChangeTutorialLocationDialog
                open={showChangeLocationDialog}
                item={selectedExplanation}
                type="explanation"
                onClose={() => {
                    setShowChangeLocationDialog(false);
                    setSelectedExplanation(null);
                }}
                onSaved={() => {
                    setShowChangeLocationDialog(false);
                    setSelectedExplanation(null);
                    getAllExplanations();
                }}
            />
        </div>
    );
};

class AllTutorialsAndExplanations extends Component {

    helpTextModel = new HelpText();

    constructor(props) {
        super(props);
        this.state = {
            activeTab: 0,
            tutorialCount: null,
            explanationCount: null,
        };
    }

    render() {
        const { activeTab } = this.state;
        return (
            <SecureShow targetRole={1}>
                <div style={{ marginTop: '-3.5rem' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, val) => this.setState({ activeTab: val })}
                        sx={{
                            px: 2,
                            pt: 1,
                            borderBottom: '1px solid #e0e0e0',
                            '& .MuiTab-root': {
                                fontSize: '14px',
                                textTransform: 'none',
                                color: '#646a6e',
                                minWidth: 120,
                                '&.Mui-selected': {
                                    color: '#bb0000',
                                },
                            },
                        }}
                        TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: '#bb0000' } }}
                    >
                        <Tab label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                Tutorials
                                <Tooltip title={
                                    <div style={{fontSize:'13px'}}>
                                        {this.helpTextModel.getHelpTextForKey('Tutorials')}
                                    </div>
                                } arrow placement="top">
                                    <span style={{ display: 'flex', alignItems: 'center' }}><MdInfoOutline size={20} /></span>
                                </Tooltip>
                            </span>
                        } />
                        <Tab label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                Explanations
                                <Tooltip title={<div style={{fontSize:'13px'}}>
                                    {this.helpTextModel.getHelpTextForKey('Explanations')}
                                </div>} arrow placement="top">
                                    <span style={{ display: 'flex', alignItems: 'center' }}><MdInfoOutline size={20} /></span>
                                </Tooltip>
                            </span>
                        } />
                    </Tabs>
                    {activeTab === 0 && <AllTutorialsTab onCountChange={(count) => this.setState({ tutorialCount: count })} 
                            tutorialsLocationHelpText={this.helpTextModel.getHelpTextForKey('Tutorials Location')}
                        />}
                    {activeTab === 1 && <AllExplanationsTab onCountChange={(count) => this.setState({ explanationCount: count })} 
                            explanationsLocationHelpText={this.helpTextModel.getHelpTextForKey('Tutorials Location')}
                        />}
                </div>
            </SecureShow>
        );
    }
}

export default AllTutorialsAndExplanations;
